import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import avatarDE from "../assets/avatar_de.mp4";
import avatarEN from "../assets/avatar_en.mp4";
import bfLogo from "../assets/bf_logo.png";
import "./LandingPage.css";

/* ===== FIREBASE ===== */
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

const LandingPage = () => {
  const navigate = useNavigate();

  const [lang, setLang] = useState(() => {
    const browserLang = navigator.language || navigator.userLanguage || "de";
    return browserLang.startsWith("en") ? "en" : "de";
  });

  const [isPro, setIsPro] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {});
    return () => unsubscribe();
  }, []);

  const toggleVideo = () => {
    if (!videoRef.current) return;
    if (!isPlaying) {
      videoRef.current.muted = false;
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const navigateWithLang = (path) => {
    navigate(`${path}?lang=${lang}`);
  };

  const t = {
    de: {
      heroTitle: "Mobiles Projekt- &",
      heroSpan: "Workforce-Management",
      heroSub: "Die Komplettlösung für Teams im Außeneinsatz. Optimiert für Bau, Reinigung, Handwerk und Service.",
      ctaTrial: "Kostenlos starten",
      trialInfo: "14 Tage testen • Bis zu 5 Mitarbeiter inkl.",
      navLogin: "Login",
      planTitle: "Wählen Sie Ihren Erfolgsweg",
      standard: "Standard",
      pro: "PRO Version",
      comingSoon: "In Kürze",
      planSelect: "Jetzt starten",
      recommended: "Empfohlen",
      nettoInfo: "Monatliche Zahlung (12 Mon. Vertrag)",
      perMonth: "/Monat",
      basis: "Basis",
      wachstum: "Wachstum",
      profi: "Professional",
      enterprise: "Enterprise",
      enterpriseSub: "Ab 50 Mitarbeitern",
      enterpriseDesc: "Skalierbar für Tausende von Mitarbeitern. High-Performance Architektur.",
      contactUs: "Kontakt aufnehmen",
      watch: "Präsentation ansehen",
      footerRights: "Alle Rechte vorbehalten. Registered in Cyprus.",
      impressum: "Impressum",
      datenschutz: "Datenschutz",
      agb: "AGB",
      requestPrice: "In Kürze",
      features: [
        "Native Mitarbeiter App",
        "Live GPS-Karten",
        "Online- & Offline-Modus",
        "Objektplanung & Zuweisung",
        "Stundennachweis (PDF/Excel)",
        "Fotodokumentation",
        "Digitale Unterschriften",
        "Standort-Update (10-Min)"
      ],
      proFeatures: [
        "GPS Live-Tracking (1-Min.)",
        "mDM-System (Geräteverwaltung)",
        "Interner Firmen-Chat",
        "Fahrtenbuch",
        "Lager & Inventar",
        "INKLUSIVE ALLE STANDARD-FUNKTIONEN"
      ]
    },
    en: {
      heroTitle: "Mobile Project &",
      heroSpan: "Workforce Management",
      heroSub: "The complete solution for field teams. Optimized for construction, cleaning, trades, and service.",
      ctaTrial: "Start for free",
      trialInfo: "14-day trial • Up to 5 employees included",
      navLogin: "Login",
      planTitle: "Choose your success path",
      standard: "Standard",
      pro: "PRO Version",
      comingSoon: "Coming Soon",
      planSelect: "Get Started",
      recommended: "Best Value",
      nettoInfo: "Monthly payment (12-month contract)",
      perMonth: "/Month",
      basis: "Basic",
      wachstum: "Growth",
      profi: "Professional",
      enterprise: "Enterprise",
      enterpriseSub: "From 50 employees",
      enterpriseDesc: "Scalable for thousands of employees. High-performance architecture.",
      contactUs: "Contact us",
      watch: "Watch Presentation",
      footerRights: "All rights reserved. Registered in Cyprus.",
      impressum: "Imprint",
      datenschutz: "Privacy Policy",
      agb: "Terms & Conditions",
      requestPrice: "Coming Soon",
      features: [
        "Native Employee App",
        "Live GPS Maps",
        "Online & Offline Mode",
        "Project Planning & Assignment",
        "Work Hours Report (PDF/Excel)",
        "Photo Documentation",
        "Digital Signatures",
        "GPS Update (10-min)"
      ],
      proFeatures: [
        "GPS Live Tracking (1-min)",
        "mDM System (Device Management)",
        "Internal Company Chat",
        "Trip Log (Fahrtenbuch)",
        "Warehouse & Inventory",
        "ALL STANDARD FEATURES INCLUDED"
      ]
    }
  };

  const prices = {
    standard: { basis: "99€", wachstum: "199€", professional: "399€" },
    pro: { basis: t[lang].requestPrice, wachstum: t[lang].requestPrice, professional: t[lang].requestPrice }
  };

  const currentPrices = isPro ? prices.pro : prices.standard;
  const currentFeatures = isPro ? t[lang].proFeatures : t[lang].features;

  return (
    <div className="landing-body">
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo-container">
            <img src={bfLogo} alt="BF" className="bf-nav-logo" />
            <div className="logo-text">BUILDFORCE <span>AL</span></div>
          </div>
          <div className="nav-right">
            <div className="lang-switcher">
              <span onClick={() => setLang("en")} className={lang === "en" ? "active-lang" : "inactive-lang"}>EN</span>
              <span className="lang-divider">|</span>
              <span onClick={() => setLang("de")} className={lang === "de" ? "active-lang" : "inactive-lang"}>DE</span>
            </div>
            <button className="login-nav-btn" onClick={() => navigateWithLang("/login")}>{t[lang].navLogin}</button>
          </div>
        </div>
      </nav>

      <header className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <div className="market-tag">Mobile Workforce ERP</div>
            <h1>{t[lang].heroTitle} <br/> <span>{t[lang].heroSpan}</span></h1>
            <p>{t[lang].heroSub}</p>
            <button className="cta-btn" onClick={() => navigateWithLang("/login")}>{t[lang].ctaTrial}</button>
            <p className="no-card">{t[lang].trialInfo}</p>
          </div>

          <div className="avatar-wrapper">
            <div className="avatar-container" onClick={toggleVideo}>
              <video key={lang} ref={videoRef} className="avatar-video" autoPlay muted loop={!isPlaying} playsInline onEnded={() => setIsPlaying(false)}>
                <source src={lang === "de" ? avatarDE : avatarEN} type="video/mp4" />
              </video>
              <div className="video-mask left"></div>
              <div className="video-mask right"></div>
              {!isPlaying && (
                <div className="play-overlay">
                  <div className="play-button-circle"><div className="play-triangle"></div></div>
                  <p>{t[lang].watch}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <section className="pricing-section">
        <div className="pricing-container">
          <h2 className="section-title">{t[lang].planTitle}</h2>
          
          <div className="toggle-container">
            <span className={!isPro ? "active-mode" : ""} onClick={() => setIsPro(false)} style={{cursor: 'pointer'}}>{t[lang].standard}</span>
            <div className={`switch ${isPro ? "pro-active" : ""}`} onClick={() => setIsPro(!isPro)}>
              <div className="knob"></div>
            </div>
            <span className={isPro ? "active-mode pro-text" : ""} onClick={() => setIsPro(true)} style={{cursor: 'pointer'}}>{t[lang].pro}</span>
          </div>

          <div className="pricing-grid">
            <PlanCard 
              t={t[lang]} 
              title={t[lang].basis} 
              price={currentPrices.basis} 
              limits={`5 ${lang==='de'?'Mitarbeiter':'Employees'} + 1 Admin`} 
              isPro={isPro} 
              onSelect={() => navigateWithLang("/login")} 
              feat={currentFeatures} 
            />
            <PlanCard 
              t={t[lang]} 
              title={t[lang].wachstum} 
              price={currentPrices.wachstum} 
              limits={`15 ${lang==='de'?'Mitarbeiter':'Employees'} + 2 Admins`} 
              isPro={isPro} 
              onSelect={() => navigateWithLang("/login")} 
              featured={!isPro} 
              badge={t[lang].recommended} 
              feat={currentFeatures} 
            />
            <PlanCard 
              t={t[lang]} 
              title={t[lang].profi} 
              price={currentPrices.professional} 
              limits={`35 ${lang==='de'?'Mitarbeiter':'Employees'} + 3 Admins`} 
              isPro={isPro} 
              onSelect={() => navigateWithLang("/login")} 
              feat={currentFeatures} 
            />
            
            <div className="price-card enterprise">
              <div className="plan-name">{t[lang].enterprise}</div>
              <div className="price" style={{fontSize: '2.5rem'}}>Custom</div>
              <div className="plan-limits">{t[lang].enterpriseSub}</div>
              <p className="enterprise-desc">{t[lang].enterpriseDesc}</p>
              <ul className="features">
                <li>✓ Dedicated Server</li>
                <li>✓ Full API Access</li>
                <li>✓ Personal Manager</li>
                <li>✓ Custom Onboarding</li>
              </ul>
              <div className="btn-wrapper">
                <button className="plan-btn featured-btn" onClick={() => navigateWithLang("/login")}>{t[lang].contactUs}</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-left">
            <div className="footer-logo-container">
              <img src={bfLogo} alt="BF" className="bf-footer-logo" />
              <div className="footer-logo-text">BUILDFORCE <span>AL</span></div>
            </div>
            <p>© 2026 BuildForce AL. {t[lang].footerRights}</p>
          </div>
          <div className="footer-right">
            <span className="legal-link" onClick={() => navigateWithLang("/impressum")}>{t[lang].impressum}</span>
            <span className="legal-link" onClick={() => navigateWithLang("/datenschutz")}>{t[lang].datenschutz}</span>
            <span className="legal-link" onClick={() => navigateWithLang("/agb")}>{t[lang].agb}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PlanCard = ({ t, title, price, limits, isPro, onSelect, featured, badge, feat }) => (
  <div className={`price-card ${featured ? "featured" : ""}`}>
    {featured && <div className="popular-tag">{badge}</div>}
    <div className="plan-name">{title}</div>
    
    <div className="price" style={isPro ? { 
        fontSize: '2.2rem', 
        color: '#6c757d', 
        textShadow: 'none', 
        fontWeight: '600', 
        marginBottom: '10px'
    } : { 
        fontSize: '2.5rem' 
    }}>
      {price} 
      {!isPro && <span>{t.perMonth}</span>}
    </div>

    <div className="plan-limits">{limits}</div>
    
    <ul className="features">
      {feat.map((f, i) => {
        const isLastProLine = isPro && i === feat.length - 1;
        
        if (isLastProLine) {
          return (
            <li key={i} style={{ 
              fontWeight: '800',
              color: '#4db8ff', 
              backgroundColor: 'rgba(77, 184, 255, 0.15)',
              padding: '12px 8px',
              borderRadius: '10px',
              textAlign: 'center',
              fontSize: '0.75rem',
              marginTop: '15px',
              listStyle: 'none',
              display: 'block',
              border: '1px border rgba(77, 184, 255, 0.3)'
            }}>
              ★ {f}
            </li>
          );
        }

        return (
          <li key={i} style={{ fontSize: '0.85rem' }}>
            ✓ {f}
          </li>
        );
      })}
    </ul>

    <div className="btn-wrapper">
      <button 
        className={`plan-btn ${isPro ? "disabled" : ""}`} 
        onClick={() => !isPro && onSelect()} 
        disabled={isPro}
        style={{ 
            cursor: isPro ? 'not-allowed' : 'pointer', 
            opacity: isPro ? 0.3 : 1, 
            background: isPro ? '#444' : '', 
            color: isPro ? '#888' : '' 
        }}
      >
        {isPro ? t.comingSoon : t.planSelect}
      </button>
      
      <p className="billing-info" style={{ marginTop: '15px', visibility: isPro ? 'hidden' : 'visible' }}>
         {t.nettoInfo}
      </p>
    </div>
  </div>
);

export default LandingPage;